import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  type SidebarClockMode,
  readStoredSidebarClockMode,
  writeStoredSidebarClockMode,
} from "@/lib/sidebarClock";
import { normalizeBackendUrl } from "@/lib/api";

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
const TOKEN_KEY = "token";

interface SidebarClockContextValue {
  mode: SidebarClockMode;
  showSidebarClock: boolean;
  useLocalTime: boolean;
  setMode: (mode: SidebarClockMode, options?: { persistRemote?: boolean }) => void;
  setShowSidebarClock: (show: boolean, options?: { persistRemote?: boolean }) => void;
  setUseLocalTime: (local: boolean, options?: { persistRemote?: boolean }) => void;
}

const SidebarClockContext = createContext<SidebarClockContextValue>({
  mode: "utc",
  showSidebarClock: true,
  useLocalTime: false,
  setMode: () => {},
  setShowSidebarClock: () => {},
  setUseLocalTime: () => {},
});

export function SidebarClockProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<SidebarClockMode>(() => readStoredSidebarClockMode());
  const lastVisibleModeRef = useRef<"utc" | "local">(
    readStoredSidebarClockMode() === "local" ? "local" : "utc",
  );

  const persistMode = useCallback((next: SidebarClockMode) => {
    writeStoredSidebarClockMode(next);
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sidebarClockMode: next }),
    }).catch(() => {});
  }, []);

  const setMode = useCallback((next: SidebarClockMode, options?: { persistRemote?: boolean }) => {
    if (next === "utc" || next === "local") {
      lastVisibleModeRef.current = next;
    }
    setModeState(next);
    writeStoredSidebarClockMode(next);
    if (options?.persistRemote === false) return;
    persistMode(next);
  }, [persistMode]);

  const setShowSidebarClock = useCallback((show: boolean, options?: { persistRemote?: boolean }) => {
    setMode(show ? lastVisibleModeRef.current : "hidden", options);
  }, [setMode]);

  const setUseLocalTime = useCallback((local: boolean, options?: { persistRemote?: boolean }) => {
    setMode(local ? "local" : "utc", options);
  }, [setMode]);

  const value = useMemo(
    () => ({
      mode,
      showSidebarClock: mode !== "hidden",
      useLocalTime: mode === "local",
      setMode,
      setShowSidebarClock,
      setUseLocalTime,
    }),
    [mode, setMode, setShowSidebarClock, setUseLocalTime],
  );

  return (
    <SidebarClockContext.Provider value={value}>
      {children}
    </SidebarClockContext.Provider>
  );
}

export function useSidebarClock() {
  return useContext(SidebarClockContext);
}
